import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { FaEnvelope, FaLinkedin, FaArrowLeft } from 'react-icons/fa';
import Logo from '../../../components/Logo';
import ConfirmationModal from '../../../components/ConfirmationModal';

const styles = {
  backdropBlur: {
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    backgroundColor: 'rgba(255, 255, 255, 0.6)'
  },
  transition: {
    transition: 'all 0.3s ease-in-out'
  },
  topBannerVisible: {
    transform: 'translateY(0)',
    opacity: 1,
    visibility: 'visible'
  },
  topBannerHidden: {
    transform: 'translateY(-100%)',
    opacity: 0,
    visibility: 'hidden'
  }
};

export default function BookingNavbar() {
  const { slug } = useParams();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showTopBanner, setShowTopBanner] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Add state to track current step
  const [currentStep, setCurrentStep] = useState(() => {
    const stepParam = new URLSearchParams(window.location.search).get('step');
    return parseInt(stepParam) || 1;
  });

  const [showModal, setShowModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const openModal = (e) => {
    if (e) e.preventDefault();
    setPendingNavigation('/');
    setShowModal(true);
  };

  const confirmNavigation = () => {
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
    setShowModal(false);
  };

  const cancelNavigation = () => {
    setShowModal(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
      const topBannerVisible = window.scrollY < 50;
      setShowTopBanner(topBannerVisible);
      
      const event = new CustomEvent('navbarHeightChanged', { 
        detail: { height: topBannerVisible ? 120 : 80 } 
      });
      window.dispatchEvent(event);
    };
    
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Enhanced step change handler with logging
  useEffect(() => {
    const handleStepChange = (e) => {
      if (e.detail && e.detail.step) {
        setCurrentStep(e.detail.step);
      }
    };
    
    window.addEventListener('stepChanged', handleStepChange);
    
    // Force update on mount
    const currentUrlStep = parseInt(new URLSearchParams(window.location.search).get('step')) || 1;
    setCurrentStep(currentUrlStep);
    
    return () => window.removeEventListener('stepChanged', handleStepChange);
  }, []);
  
  // Log when current step changes
  useEffect(() => {
    //console.log("BookingNavbar - Current step:", currentStep);
  }, [currentStep]);

  // Also update when URL changes
  useEffect(() => {
    const stepParam = new URLSearchParams(location.search).get('step');
    if (stepParam) {
      setCurrentStep(parseInt(stepParam));
    }
  }, [location.search]);

  const socialLinks = [
    { icon: <FaLinkedin />, href: "https://www.linkedin.com/company/pine-software/" }
  ];

  // İlerleme çubuğunun adım sayısını ve geçerli adımı doğru şekilde hesapla
  
  
  // Toplam adım sayısı (güncel akış: 3 adım)
  const totalSteps = 3;

  // no beforeunload effect here — navigation confirmation handled via modal only
  

  return (
    <>
      <div 
        className="bg-primary fixed w-full z-50"
        style={{
          height: '40px',
          ...styles.transition,
          ...(showTopBanner ? styles.topBannerVisible : styles.topBannerHidden)
        }}
      >
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap items-center justify-between h-full">
          <div className="flex items-center space-x-4 text-sm">
            <a 
              href="mailto:iletisim@pinerandevu.com" 
              className="flex items-center text-white hover:text-gray-300 text-xs sm:text-sm"
              style={{ transition: 'all 0.3s ease' }}
            >
              <FaEnvelope className="mr-1 sm:mr-2" />
              <span className="inline">iletisim@pinerandevu.com</span>
            </a>
          </div>
          <div className="flex items-center space-x-3 sm:space-x-6">
            {socialLinks.map((social, index) => (
              <a
                key={index}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-gray-300"
                style={{ transition: 'all 0.3s ease' }}
              >
                {social.icon}
              </a>
            ))}
          </div>
        </div>
      </div>

      <nav
        className={`fixed w-full z-40 ${isScrolled ? "shadow-lg" : ""}`}
        style={{
          top: showTopBanner ? '40px' : '0',
          transition: 'top 0.3s ease-in-out',
          ...styles.backdropBlur
        }}
      >
        <div className="max-w-7xl mx-auto px-4 h-20 flex items-center">
          <div className="w-full flex items-center justify-between md:justify-center">
            {slug && (
              <button
                type="button"
                onClick={(e) => openModal(e)}
                className="text-gray-600 hover:text-primary transition-colors md:absolute md:left-4 flex items-center gap-2"
              >
                <FaArrowLeft size={16} />
                <span>İşletme Profiline Dön</span>
              </button>
            )}
            
            <div className="text-xl font-bold text-gray-900 transition-opacity hover:opacity-80">
              <button
                type="button"
                onClick={(e) => openModal(e)}
                className="focus:outline-none"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <Logo />
              </button>
            </div>
          </div>
        </div>

        <div className="w-full h-1 bg-gray-200">
          <div 
            className="h-1 bg-primary transition-all duration-300"
            style={{ 
              width: `${(currentStep / totalSteps) * 100}%`,
              opacity: 1
            }}
            data-step={currentStep} 
          />
        </div>
      </nav>

      <ConfirmationModal
        isOpen={showModal}
        onConfirm={confirmNavigation}
        onClose={cancelNavigation}
        title="Sayfadan Ayrılmak İstiyor musunuz?"
        message="Randevu oluşturma işleminiz yarıda kalacak ve girdiğiniz bilgiler kaybolacak. Devam etmek istiyor musunuz?"
      />
    </>
  );
}
